/**
 * Document Preview Component
 * Shows different preview modes: markdown with KaTeX math, LaTeX PDF, or plain text
 */

import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { marked } from 'marked'
import { markedHighlight } from 'marked-highlight'
import hljs from 'highlight.js'
import DOMPurify from 'dompurify'
import clsx from 'clsx'
import KaTeX from 'katex'
import 'katex/dist/katex.min.css'

import { latexAPI, LatexCompilationError } from '../../api/latex'
import { log, logError, logWarn, logDebug } from '@utils/logger'
import PDFPreview from './PDFPreview'

interface DocumentPreviewProps {
  content: string
  mode: 'markdown' | 'latex' | 'plain'
  className?: string
  documentId?: string // For LaTeX projects, use project compilation
  documentType?: string // Document type to determine compilation method
  currentFilePath?: string // Path to currently opened file (for LaTeX projects)
  autoCompile?: boolean // Whether to auto-compile on content changes (default: true)
  compiledPdfBlob?: Blob | null // Pre-compiled PDF blob from parent (prevents double compilation)
  compilationErrors?: string[] // Compilation errors from parent
  compilationWarnings?: string[] // Compilation warnings from parent
  onToggleDebugPanel?: () => void // Callback to toggle debug panel
  isDebugPanelOpen?: boolean // Whether debug panel is open
  navigateToPdfPage?: number | null // Navigate to specific PDF page
  onNavigateToSource?: (filePath: string, line: number, column: number) => void // Callback for SyncTeX navigation
  mainFilePathForSyncTeX?: string // File path that the PDF was compiled for (for SyncTeX lookup)
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({ 
  content, 
  mode, 
  className,
  documentId,
  documentType,
  currentFilePath,
  autoCompile = true,
  compiledPdfBlob: externalPdfBlob,
  compilationErrors = [],
  compilationWarnings = [],
  onToggleDebugPanel,
  isDebugPanelOpen = false,
  navigateToPdfPage = null,
  onNavigateToSource,
  mainFilePathForSyncTeX
}) => {
  // Debug logging for navigateToPdfPage prop
  useEffect(() => {
    if (mode === 'latex') {
      logDebug('[DocumentPreview] navigateToPdfPage prop changed:', navigateToPdfPage)
    }
  }, [navigateToPdfPage, mode])
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [isCompiling, setIsCompiling] = useState(false)
  const [compilationError, setCompilationError] = useState<string>('')
  const [lastCompiledContent, setLastCompiledContent] = useState<string>('')
  
  // Use external PDF blob if provided (from handleCompile), otherwise use internal state
  // If externalPdfBlob is explicitly provided (even if null), use it; otherwise use internal pdfBlob
  const effectivePdfBlob = externalPdfBlob !== undefined ? externalPdfBlob : pdfBlob
  
  // Debug logging
  useEffect(() => {
    if (mode === 'latex') {
      logDebug('[DocumentPreview] PDF blob state:', {
        externalPdfBlob: externalPdfBlob ? 'provided' : externalPdfBlob === null ? 'null' : 'undefined',
        internalPdfBlob: pdfBlob ? 'exists' : 'null',
        effectivePdfBlob: effectivePdfBlob ? 'exists' : 'null',
        isCompiling
      })
    }
  }, [externalPdfBlob, pdfBlob, effectivePdfBlob, isCompiling, mode])

  useEffect(() => {
    if (mode !== 'latex') return
    console.log('[DocumentPreview] effectivePdfBlob update', {
      externalProvided: externalPdfBlob ? true : false,
      externalExplicitNull: externalPdfBlob === null,
      internalAvailable: !!pdfBlob,
      effectiveAvailable: !!effectivePdfBlob,
      internalSize: pdfBlob?.size,
      externalSize: externalPdfBlob?.size
    })
  }, [mode, effectivePdfBlob, pdfBlob, externalPdfBlob])

  // Configure marked for syntax highlighting
  const markedInstance = useMemo(() => {
    return marked.use(
      markedHighlight({
        langPrefix: 'hljs language-',
        highlight(code, lang) {
          const language = hljs.getLanguage(lang) ? lang : 'plaintext'
          return hljs.highlight(code, { language }).value
        }
      })
    )
  }, [])

  // Compile LaTeX using backend server
  const compileLatexContent = useCallback(async (latexCode: string) => {
    if (!latexCode.trim()) {
      setPdfBlob(null)
      setCompilationError('')
      return
    }

    // Don't recompile if content hasn't changed
    if (latexCode === lastCompiledContent && pdfBlob) {
      return
    }

    setIsCompiling(true)
    setCompilationError('')
    
    try {
      let pdfBlob: Blob
      
      // For LaTeX projects with documentId, use project compilation (has access to all files)
      if (documentId && documentType === 'latex') {
        // Determine which file to compile
        // If currentFilePath is a .tex file, compile that; otherwise use main.tex
        let mainFilePath: string | undefined = undefined
        if (currentFilePath && currentFilePath.endsWith('.tex')) {
          mainFilePath = currentFilePath
          logDebug('[DocumentPreview] Compiling specific file:', mainFilePath)
        } else {
          logDebug('[DocumentPreview] Compiling main.tex (default)')
        }
        
        // Auto-detect if BibTeX is needed:
        // 1. Check if LaTeX content contains bibliography commands
        const hasBibliographyCommands = /\\(?:bibliography|addbibresource|printbibliography|cite)/i.test(latexCode)
        
        // 2. Recursively check if project has .bib files (including nested folders)
        const checkForBibFiles = (files: any[]): boolean => {
          for (const file of files || []) {
            if (file.name.endsWith('.bib')) {
              return true
            }
            if (file.children && file.children.length > 0) {
              if (checkForBibFiles(file.children)) {
                return true
              }
            }
          }
          return false
        }
        
        let hasBibFiles = false
        try {
          const fileTree = await latexAPI.getFileTree(documentId)
          hasBibFiles = checkForBibFiles(fileTree.files || [])
        } catch (error) {
          logWarn('[DocumentPreview] Could not check for .bib files:', error)
        }
        
        // Enable BibTeX if bibliography commands are used or .bib files exist
        const shouldIncludeBibtex = hasBibliographyCommands || hasBibFiles
        
        logDebug('[DocumentPreview] BibTeX detection:', { 
          hasBibliographyCommands, 
          hasBibFiles, 
          shouldIncludeBibtex 
        })
        
        // Note: DocumentPreview doesn't have access to user preferences
        // The compiler preference is handled in EditorPage's handleCompile
        // For standalone preview, default to pdflatex
        const compileResult = await latexAPI.compileProject(documentId, {
          compiler: 'pdflatex', // Will use user preference from backend if not specified
          runs: 2,
          include_bibtex: shouldIncludeBibtex,
          main_file_path: mainFilePath
        })
        
        // Convert base64 PDF data to Blob if available
        // Optimized base64 decoding using modern browser APIs
        if (compileResult && compileResult.pdf_data) {
          try {
            // Use fetch API for efficient base64 decoding (handles large data better)
            // This is faster than manual character-by-character conversion
            const base64Data = compileResult.pdf_data
            const binaryString = atob(base64Data)
            
            // Use Uint8Array.from with map for better performance than manual loop
            // Modern browsers optimize this better than manual iteration
            const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0))
            pdfBlob = new Blob([bytes], { type: 'application/pdf' })
            
            logDebug('[DocumentPreview] PDF decoded successfully', {
              originalSize: base64Data.length,
              decodedSize: bytes.length,
              blobSize: pdfBlob.size
            })
          } catch (error) {
            logError('[DocumentPreview] Failed to convert base64 PDF data to Blob:', error)
            throw new LatexCompilationError(
              'Failed to process PDF data',
              (compileResult && Array.isArray(compileResult.errors)) ? compileResult.errors : [],
              (compileResult && Array.isArray(compileResult.warnings)) ? compileResult.warnings : []
            )
          }
        } else {
          const normalizedErrors = (compileResult && Array.isArray(compileResult.errors))
            ? compileResult.errors
            : [];
          const normalizedWarnings = (compileResult && Array.isArray(compileResult.warnings))
            ? compileResult.warnings
            : [];
          throw new LatexCompilationError(
            compileResult.message || 'PDF not generated',
            normalizedErrors,
            normalizedWarnings
          )
        }
      } else {
        // For standalone LaTeX content, use content-based compilation
        logDebug('[DocumentPreview] Using content-based compilation')
        pdfBlob = await latexAPI.compileLatexToPdf({
          content: latexCode,
          compiler: 'pdflatex',
          runs: 2
        })
      }
      
      setPdfBlob(pdfBlob)
      setLastCompiledContent(latexCode)
    } catch (error) {
      logError('LaTeX compilation error:', error)
      if (error instanceof LatexCompilationError) {
        setCompilationError(`${error.message}\n\nErrors:\n${error.errors.join('\n')}\n\nWarnings:\n${error.warnings.join('\n')}`)
      } else {
        setCompilationError(`Compilation failed: ${(error as Error).message}`)
      }
      setPdfBlob(null)
    } finally {
      setIsCompiling(false)
    }
  }, [documentId, documentType, currentFilePath])

  // Process math expressions in markdown using KaTeX
  const processMarkdownWithMath = (text: string): string => {
    // Handle block math ($$...$$)
    text = text.replace(/\$\$([^$]*?)\$\$/g, (_match, math) => {
      try {
        const html = KaTeX.renderToString(math.trim(), {
          displayMode: true,
          throwOnError: false
        })
        return `<div class="math-display">${html}</div>`
      } catch (error) {
        return `<div class="text-red-500">Error rendering math: ${math}</div>`
      }
    })

    // Handle inline math ($...$)
    text = text.replace(/\$([^$\n]+?)\$/g, (_match, math) => {
      try {
        const html = KaTeX.renderToString(math.trim(), {
          displayMode: false,
          throwOnError: false
        })
        return `<span class="math-inline">${html}</span>`
      } catch (error) {
        return `<span class="text-red-500">Error: ${math}</span>`
      }
    })

    return text
  }

  // Trigger LaTeX compilation when content changes in LaTeX mode (only if autoCompile is enabled)
  // DISABLED: Auto-compile is disabled - compilation only happens when user clicks compile button
  // useEffect(() => {
  //   if (mode === 'latex' && content.trim() && autoCompile) {
  //     // Debounce compilation to avoid too many requests
  //     const timer = setTimeout(() => {
  //       compileLatexContent(content)
  //     }, 1000) // Wait 1 second after user stops typing

  //     return () => clearTimeout(timer)
  //   }
  // }, [content, mode, autoCompile])

  // Update internal pdfBlob when external blob changes (from handleCompile)
  // Use a ref to track the previous blob to detect actual changes
  const prevExternalBlobRef = React.useRef<Blob | null | undefined>(undefined)

  // CRITICAL: Clear PDF blob immediately when documentId changes
  // This prevents showing the previous document's PDF when switching documents
  useEffect(() => {
    if (documentId) {
      // When documentId changes, clear internal PDF state immediately
      // The parent (EditorPage) will also clear externalPdfBlob, but we clear here too
      // to ensure no stale PDF is shown during the transition
      setPdfBlob(null)
      setCompilationError('')
      setIsCompiling(false)
      setLastCompiledContent('')
      prevExternalBlobRef.current = undefined
      logDebug('[DocumentPreview] Document ID changed, cleared PDF blob', { documentId })
    }
  }, [documentId])
  useEffect(() => {
    // Only update if the blob actually changed (different reference or null state)
    const blobChanged = prevExternalBlobRef.current !== externalPdfBlob
    
    if (externalPdfBlob !== undefined && blobChanged) {
      if (externalPdfBlob !== null) {
        // Use the blob directly - don't create a new one as it might cause issues
        // React will detect the change if the reference is different
        setPdfBlob(externalPdfBlob)
        setLastCompiledContent(content) // Mark as compiled
        setCompilationError('') // Clear any previous errors
        setIsCompiling(false) // Clear compiling state
        prevExternalBlobRef.current = externalPdfBlob
        logDebug('[DocumentPreview] Using external PDF blob from parent', {
          blobSize: externalPdfBlob.size,
          blobType: externalPdfBlob.type
        })
      } else {
        // Explicitly null means clear the PDF
        setPdfBlob(null)
        setCompilationError('')
        setIsCompiling(false)
        prevExternalBlobRef.current = null
        logDebug('[DocumentPreview] External PDF blob is null, clearing PDF')
      }
    }
  }, [externalPdfBlob, content])


  const renderedContent = useMemo(() => {
    if (!content.trim()) {
      return '<div class="text-gray-500 italic">Start writing to see preview...</div>'
    }

    switch (mode) {
      case 'markdown': {
        try {
          const processedContent = processMarkdownWithMath(content)
          const html = markedInstance.parse(processedContent)
          return DOMPurify.sanitize(html, {
            ADD_TAGS: ['span', 'div'],
            ADD_ATTR: ['class']
          })
        } catch (error) {
          logError('Markdown parsing error:', error)
          return '<div class="text-red-500">Error parsing markdown</div>'
        }
      }

      case 'latex': {
        // LaTeX mode will show PDF preview, not HTML
        return null
      }

      case 'plain': {
        return DOMPurify.sanitize(
          `<p>${content
            .replace(/\n\n/g, '</p><p>')
            .replace(/\n/g, '<br>')}</p>`
        )
      }

      default:
        return content
    }
  }, [content, mode, markedInstance])

  const handlePdfDownload = () => {
    if (pdfBlob) {
      latexAPI.downloadPdf(pdfBlob, 'document.pdf')
    }
  }

  const handlePdfOpenExternal = () => {
    if (pdfBlob) {
      latexAPI.openPdfInNewTab(pdfBlob)
    }
  }


  return (
    <div className={clsx('h-full overflow-hidden', className)}>
      {mode === 'latex' ? (
        // LaTeX mode: Show PDF preview
        <div className="h-full flex flex-col relative">
          <PDFPreview
            pdfBlob={effectivePdfBlob ?? undefined}
            isLoading={isCompiling}
            error={compilationError || undefined}
            onDownload={handlePdfDownload}
            onOpenExternal={handlePdfOpenExternal}
            className="h-full"
            navigateToPage={navigateToPdfPage}
            compilationErrors={compilationErrors}
            compilationWarnings={compilationWarnings}
            onToggleDebugPanel={onToggleDebugPanel}
            isDebugPanelOpen={isDebugPanelOpen}
            documentId={documentId}
            mainFilePath={mainFilePathForSyncTeX || currentFilePath || undefined}
            onNavigateToSource={onNavigateToSource}
          />
        </div>
      ) : (
        // Other modes: Show HTML preview
        <div className="h-full overflow-auto">
          <div className="max-w-none p-6 bg-white">
            <article 
              className="document-preview prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: renderedContent || '' }}
            />
          </div>
        </div>
      )}
      
      {/* Print and math styles */}
      <style>{`
        @media print {
          .document-preview {
            font-size: 12pt;
            line-height: 1.5;
          }
          
          .document-preview h1,
          .document-preview h2,
          .document-preview h3 {
            page-break-after: avoid;
          }
          
          .document-preview blockquote,
          .document-preview pre {
            page-break-inside: avoid;
          }
        }

        /* Math display styles */
        .math-display {
          margin: 1em 0;
          text-align: center;
          overflow-x: auto;
        }

        .math-inline {
          display: inline;
        }
      `}</style>
    </div>
  )
}

export default DocumentPreview